import { useDeferredValue, useEffect, useMemo, useState } from "react";

type SearchableOption = {
    readonly label: string;
};

type HookValue<T extends SearchableOption> = {
    readonly searchTerm: string;
    readonly filteredOptions: readonly T[];
    readonly onSearchTermChange: (term: string) => void;
};

const normalizeForSearch = (value: string): string => value.toLowerCase();

export const useComboboxSearch = <T extends SearchableOption>(options: readonly T[], isOpen: boolean): HookValue<T> => {
    const [searchTerm, setSearchTerm] = useState("");

    const deferredSearchTerm = useDeferredValue(searchTerm);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
        }
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        const term = normalizeForSearch(deferredSearchTerm).trim();

        if (!term) {
            return options;
        }

        return options.filter((option) => normalizeForSearch(option.label).includes(term));
    }, [options, deferredSearchTerm]);

    return { searchTerm, filteredOptions, onSearchTermChange: setSearchTerm };
};
